import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../Navbar";
import ProBlogCard from "./ProBlogCard";
import {
  getCreatorProPage,
  payForCreatorAccess,
  formatDate,
  formatRemaining,
} from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// A CREATOR'S PRO PAGE  (Steps 6, 7, 8, 9)
//
// What a reader lands on from "Watch Pro" on the free author profile, and what
// the ₹19 payment is made from.
//
// WHAT IT SHOWS
//   • the creator's identity — the SAME picture and name as their free
//     profile, because it is the same person (Step 6: nothing is entered
//     twice);
//   • exactly two counters: Pro blogs, and paid users (Step 6). There is no
//     analytics board here — the six-tile one this page used to have was what
//     Part B item 1 removed;
//   • the newest two blogs open, everything older locked (Step 8);
//   • a Pay ₹19 button that unlocks THIS creator and no one else (Step 9).
//
// WHAT DECIDES LOCKED
//   Not this file. The server sent each blog already resolved to locked or
//   unlocked for this reader, and a locked one arrived with no body, no image
//   URLs and no video URL. This page renders what it was given.
// =====================================================

const initialOf = (name) => (name || "?").trim().charAt(0).toUpperCase();

function ProCreatorProfile() {
  const { creatorId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [toast, setToast] = useState(null);

  const username = localStorage.getItem("username") || "";
  const userPic = localStorage.getItem("profilePic") || "";

  const flash = useCallback((msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3800);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCreatorProPage(creatorId);
      setData(res);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load this Pro page");
    } finally {
      setLoading(false);
    }
  }, [creatorId]);

  useEffect(() => {
    load();
  }, [load]);

  // ── PAY ₹19 (Step 9) ──
  // The amount and the creator are set by the SERVER when it creates the
  // order; this only says which creator's page the reader is on. On success
  // the page is reloaded from the server rather than patched locally, so what
  // unlocks is what the server says unlocked.
  const unlock = useCallback(() => {
    if (paying || !data) return;
    setPaying(true);

    payForCreatorAccess({
      creatorId,
      creatorName: data.creator?.name,
      userName: username,

      onSuccess: async (res) => {
        setPaying(false);
        flash(res.message || "Unlocked!", "success");
        await load();
      },

      onError: (msg) => {
        setPaying(false);
        flash(msg, "error");
      },

      onClose: () => setPaying(false),
    });
  }, [paying, data, creatorId, username, flash, load]);

  if (loading) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-loading-screen">
          <div className="pro-spinner" />
          <p>Loading Pro page…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-container">
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { creator, stats, blogs, myAccess, isOwner, unlockPriceRupees, freePreviewCount } = data;

  const hasAccess = isOwner || myAccess?.status === "active";
  const lockedCount = blogs.filter((b) => b.locked).length;

  return (
    <div className="pro-page-wrap">
      <Navbar />

      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="pro-container">
        {/* ── CREATOR IDENTITY — the same profile as the free side ── */}
        <section className="pro-card pro-creator-head">
          <div className="pro-avatar">
            {creator.profilePic ? (
              <img src={creator.profilePic} alt={creator.name} />
            ) : (
              <span className="pro-avatar-fallback">{initialOf(creator.name)}</span>
            )}
            <span className="pro-avatar-badge" title="Pro creator">💎</span>
          </div>

          <div className="pro-creator-meta">
            <h1 className="pro-creator-name">{creator.name}</h1>
            <p className="pro-creator-tag">💎 TheBlogsSphere Pro Creator</p>
            {creator.bio && <p className="pro-creator-bio">{creator.bio}</p>}

            {/* ── STEP 6 — exactly two counters ── */}
            <div className="pro-stat-grid pro-stat-grid-2">
              <div className="pro-stat">
                <strong>{stats.blogCount}</strong>
                <small>Pro Blogs</small>
              </div>
              <div className="pro-stat">
                <strong>{stats.paidUserCount}</strong>
                <small>Paid Users</small>
              </div>
            </div>
          </div>
        </section>

        {/* ── ACCESS STRIP (Steps 9, 10, 11) ── */}
        {!isOwner && (
          <section className="pro-card pro-access-bar">
            {hasAccess ? (
              <>
                <div className="pro-access-state">
                  <span className="pro-chip pro-chip-live">🟢 Access active</span>
                  <span className="pro-chip">
                    Expires <strong>{formatDate(myAccess.expiryDate)}</strong>
                  </span>
                  <span className="pro-chip">
                    <strong>{formatRemaining(myAccess.remainingMs)}</strong> left
                  </span>
                </div>
                <p className="pro-access-note">
                  All of {creator.name}&rsquo;s Pro blogs are unlocked for you.
                </p>
              </>
            ) : (
              <>
                <div className="pro-access-state">
                  <h2 className="pro-access-title">
                    Unlock {creator.name}&rsquo;s Pro blogs
                  </h2>
                  <p className="pro-access-note">
                    The newest {freePreviewCount} blogs are free to read.
                    {lockedCount > 0 && (
                      <>
                        {" "}
                        <strong>{lockedCount}</strong> more{" "}
                        {lockedCount === 1 ? "is" : "are"} locked.
                      </>
                    )}{" "}
                    One payment unlocks this creator only.
                  </p>
                  {myAccess?.status === "expired" && (
                    <p className="pro-access-note pro-access-note-expired">
                      Your previous access expired on {formatDate(myAccess.expiryDate)}.
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  className="pro-btn pro-btn-primary pro-btn-lg"
                  onClick={unlock}
                  disabled={paying}
                >
                  {paying ? "Opening payment…" : `🔓 Pay ₹${unlockPriceRupees}`}
                </button>
              </>
            )}
          </section>
        )}

        {/* ── PRO BLOGS ── */}
        <section className="pro-content-section">
          <div className="pro-section-head">
            <h2>📚 Pro Blogs</h2>
            {isOwner && (
              <button
                type="button"
                className="pro-btn pro-btn-ghost"
                onClick={() => navigate("/create-content")}
              >
                ✨ Create Content
              </button>
            )}
          </div>

          {blogs.length === 0 ? (
            <div className="pro-card pro-empty">
              <p>{creator.name} has not published any Pro blogs yet.</p>
            </div>
          ) : (
            <div className="pro-post-list">
              {blogs.map((blog) => (
                <ProBlogCard
                  key={blog._id}
                  blog={blog}
                  compact
                  currentUsername={username}
                  currentUserPic={userPic}
                  unlockPriceRupees={unlockPriceRupees}
                  onUnlock={isOwner ? null : unlock}
                  onOpen={blog.locked ? null : () => navigate(`/pro-blog/${blog._id}`)}
                />
              ))}
            </div>
          )}
        </section>

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

export default ProCreatorProfile;
